import React, { useState, useEffect } from "react";
import { ClipboardList, Plus, Trash2, X, Save } from "lucide-react";
import { createAssessmentFactor,getAssessmentFactor,deleteAssessmentFactor, } from "../Services/BuildAnalytics";

const AddAssessmentFactor = ({ open, onClose, surveyUID, onSuccess }) => {
  const [assessmentFactors, setAssessmentFactors] = useState([
    {
      id: Date.now(),
      value: "",
    },
  ]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const [existingFactors, setExistingFactors] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
      if (open && surveyUID) {
        fetchExistingFactors();
      } else if (!open) {
        setExistingFactors([]);
        setConfirmDeleteId(null);
      }
    }, [open, surveyUID]);

  const fetchExistingFactors = async () => {
    try {
      setLoadingExisting(true);
     const response = await getAssessmentFactor(surveyUID);
      if (
        response.data.status &&
        response.data.status.toLowerCase() === "success"
      ) {
        setExistingFactors(response.data.assessmentFactors || []);
      }
    } catch (error) {
      // Handle error if needed
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleDeleteFactor = async (assessmentFactorID) => {
    try {
      setDeletingId(assessmentFactorID);

      const payload = {
        assessmentFactorId: assessmentFactorID,
        assessmentUId: surveyUID,
      };

      const response = await deleteAssessmentFactor(payload);

      if (
        response.data.status &&
        response.data.status.toLowerCase() === "success"
      ) {
        CustomToast("success", "Assessment Factor Deleted Successfully.");
        setConfirmDeleteId(null);
        await fetchExistingFactors();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        CustomToast("fail", response.data.message);
     }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      CustomToast("fail", apiMessage || "Something went wrong while deleting.");
    } finally {
      setDeletingId(null);
    }
  };

  const [loading, setLoading] = useState(false);
  if (!open) return null;

  const addFactor = () => {
    setAssessmentFactors((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        value: "",
      },
    ]);
  };

  const updateFactor = (id, value) => {
    setAssessmentFactors((prev) =>
      prev.map((item) => (item.id === id ? { ...item, value } : item)),
    );
  };

  const removeFactor = (id) => {
    if (assessmentFactors.length === 1) {
      return;
    }

    setAssessmentFactors((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSave = async () => {
    const factors = assessmentFactors
      .map((x) => x.value.trim())
      .filter((x) => x !== "");

    if (factors.length === 0) {
      CustomToast("fail", "Please enter at least one assessment factor.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        assessmentUID: surveyUID,
        assessmentFactors: factors,
      };

      const response = await createAssessmentFactor(payload);

      if (
        response.data.status &&
        response.data.status.toLowerCase() === "success"
      ) {
        CustomToast("success", "Assessment Factors Saved Successfully.");

        setAssessmentFactors([
          {
            id: Date.now(),
            value: "",
          },
        ]);

        if (onSuccess) {
          onSuccess();
        }

        onClose();
      } else {
        CustomToast("fail", response.data.message);
      }
    } catch (error) {
      CustomToast("fail", "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-[2px]
  transition-opacity duration-300 z-[9998]
  ${
    open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
  }`}
      />
      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[700px]
        bg-white shadow-2xl z-[9999]
        transform transition-transform duration-300 ease-in-out
        flex flex-col
        ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}

        <div className="flex items-center justify-between px-6 py-5 border-b bg-green-50">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-[#7f35b2]/10 flex items-center justify-center">
              <ClipboardList size={22} className="text-[#7f35b2]" />
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Assessment Factors
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                Create factors for this survey
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}

        <div className="flex-1 overflow-y-auto p-5">
          <div className="space-y-4">
            {assessmentFactors.map((factor, index) => (
              <div key={factor.id} className="flex gap-2">
                <input
                  type="text"
                  placeholder={`Assessment Factor ${index + 1}`}
                  value={factor.value}
                  onChange={(e) => updateFactor(factor.id, e.target.value)}
                  className="flex-1 border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#7f35b2]/20 focus:border-[#7f35b2] outline-none"
                />

                <button
                  onClick={() => removeFactor(factor.id)}
                  disabled={assessmentFactors.length === 1}
                  className="w-11 h-11 rounded-lg border hover:bg-red-50 disabled:opacity-40"
                >
                  <Trash2 size={18} className="mx-auto text-red-500" />
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={addFactor}
            className="mt-5 w-full border-2 border-dashed border-[#7f35b2] text-[#7f35b2] rounded-xl py-3 flex items-center justify-center gap-2 hover:bg-[#7f35b2]/5 transition"
          >
            <Plus size={18} />
            Add Another Factor
          </button>
          <div className="mt-8">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Existing Assessment Factors
            </h3>

            {loadingExisting ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : existingFactors.length === 0 ? (
              <p className="text-sm text-gray-400">
                No assessment factors added yet.
              </p>
            ) : (
              <div className="space-y-2">
                {existingFactors.map((factor) => (
                  <div
                    key={factor.assessmentFactorID}
                    className="flex items-center justify-between border rounded-lg px-4 py-2.5"
                  >
                    {confirmDeleteId === factor.assessmentFactorID ? (
                      <div className="flex items-center justify-between w-full gap-2">
                        <span className="text-sm text-gray-600">
                          Delete this factor?
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-xs px-3 py-1.5 rounded-md border hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteFactor(factor.assessmentFactorID)
                            }
                            disabled={deletingId === factor.assessmentFactorID}
                            className="text-xs px-3 py-1.5 rounded-md bg-red-500 text-white hover:bg-red-600 disabled:opacity-60"
                          >
                            {deletingId === factor.assessmentFactorID
                              ? "Deleting..."
                              : "Confirm"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="text-sm text-gray-800">
                          {factor.assessmentFactor}
                        </span>
                        <button
                          onClick={() =>
                            setConfirmDeleteId(factor.assessmentFactorID)
                          }
                          className="w-9 h-9 rounded-lg border hover:bg-red-50"
                          title="Delete factor"
                        >
                          <Trash2 size={16} className="mx-auto text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        
        </div>

        {/* Footer */}

        <div className="border-t bg-white px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="flex-1 border rounded-lg py-2.5 font-medium hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 bg-[#7f35b2] hover:bg-[#3bb290] text-white rounded-lg py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Save size={18} />

            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddAssessmentFactor;

